import {
  COUNTER_LEAD,
  COUNTER_PROMPT,
  MULTI,
  OPEN,
  OTHER_LABEL,
  WEIGH,
  WEIGH_PROMPT,
  WHY_LABEL,
  splitPicks,
  splitWeighed,
  type SituationQuestion,
} from "@/components/situations/answer-fields";
import { SituationStory } from "@/components/situations/situation-story";

export type PrintedQuestion = SituationQuestion & {
  mark?: string | null;
};

export type PrintedSituation = {
  key: string;
  title: string;
  themeLabels?: string[] | null;
  intro?: string | null;
  story: string;
  forGrownup?: string | null;
  questions: PrintedQuestion[];
};

/**
 * One situation on paper.
 *
 * Shared by the single-situation print view and the whole-session one, so a sheet
 * printed on its own and the same sheet inside a set cannot drift apart — which
 * they would, being two hundred lines of layout copied once.
 *
 * The discussion notes print on their own page and are off unless asked for: the
 * sheet the child writes on and the notes the grown-up reads are two different
 * documents, and a child handed the sheet must not be reading what the situation
 * is testing.
 */
export function PrintedSheet({
  situation,
  showAnswers = false,
  showNotes = false,
  onePerPage = false,
  showName = true,
  startOnNewPage = false,
}: {
  situation: PrintedSituation;
  showAnswers?: boolean;
  showNotes?: boolean;
  /** A whole page per question — room to write by hand. */
  onePerPage?: boolean;
  /** A line for a name. Wanted once per document, not once per situation. */
  showName?: boolean;
  startOnNewPage?: boolean;
}) {
  const questions = situation.questions ?? [];

  return (
    <section className={startOnNewPage ? "break-before-page" : undefined}>
      <header className="mb-6 border-b pb-4">
        <h2 className="text-2xl font-bold">{situation.title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Sprendimų laboratorija
          {situation.themeLabels && situation.themeLabels.length > 0
            ? ` · ${situation.themeLabels.join(", ")}`
            : ""}
        </p>
        {/* A name line, because these come back as loose sheets of paper. */}
        {showName && (
          <p className="mt-4 text-sm">Vardas: ______________________________</p>
        )}
      </header>

      {situation.intro && (
        <p className="mb-4 text-[0.95rem] italic">{situation.intro}</p>
      )}

      <SituationStory html={situation.story} className="mb-8" />

      <ol className="space-y-6">
        {questions.map((question, index) => (
          <li
            key={question.key}
            className={
              onePerPage && index > 0
                ? // `break-before` rather than `break-after`, and skipped on the
                  // first: a break after the last question prints a blank sheet,
                  // and the first question belongs on the story's page.
                  "break-inside-avoid break-before-page"
                : "break-inside-avoid"
            }
          >
            {/* The story is pages back once each question has its own sheet, so
                the title comes along as a running header. Repeating the story
                itself would be honest and would also treble the paper. */}
            {onePerPage && index > 0 && (
              <p className="mb-2 text-[0.8rem] text-muted-foreground">
                {situation.title}
              </p>
            )}
            <p className="font-medium">
              {index + 1}. {question.prompt}
            </p>

            <PrintedAnswer
              question={question}
              showAnswers={showAnswers}
              roomy={onePerPage}
            />
          </li>
        ))}
      </ol>

      {showNotes && situation.forGrownup && (
        <div className="mt-10 break-before-page border-t pt-6">
          <h3 className="mb-3 text-lg font-semibold">
            Suaugusiajam — „{situation.title}"
          </h3>
          <SituationStory
            html={situation.forGrownup}
            className="text-[0.95rem] [&_li]:ml-4 [&_li]:list-disc [&_ul]:space-y-1"
          />
        </div>
      )}
    </section>
  );
}

/**
 * One question's answer space on paper.
 *
 * A closed question has to print its options — a sheet showing only the question
 * is one nobody can answer away from the screen, which is the entire point of
 * printing it. The boxes are drawn empty and ticked by hand; ticked ones are
 * filled in only when a grown-up asked to see what was already written.
 */
function PrintedAnswer({
  question,
  showAnswers,
  roomy = false,
}: {
  question: PrintedQuestion;
  showAnswers: boolean;
  roomy?: boolean;
}) {
  const picks = showAnswers ? splitPicks(question.answer) : [];
  const lines = (asked: number) => (roomy ? Math.max(asked, 8) : asked);

  if (question.answerType === OPEN) {
    return (
      <>
        {showAnswers && question.answer ? (
          <p className="mt-2 whitespace-pre-wrap border-l-2 border-muted pl-3 text-[0.95rem]">
            {question.answer}
          </p>
        ) : (
          <WritingLines count={lines(question.lines)} />
        )}
        <PrintedCounter question={question} showAnswers={showAnswers} roomy={roomy} />
      </>
    );
  }

  // Every road with room to write under it, and no box to tick — there is nothing
  // to choose here, which on paper has to be visible at a glance or a child will
  // hunt for the tickbox.
  if (question.answerType === WEIGH) {
    const { notes, leftover } = splitWeighed(
      showAnswers ? question.answer : null,
      question.options
    );

    return (
      <div className="mt-2 space-y-3">
        <p className="text-[0.85rem] italic text-muted-foreground">
          Prie kiekvieno parašyk, kas nuo to pasikeistų.
        </p>
        {question.options.map((option) => (
          <div key={option} className="break-inside-avoid">
            <p className="text-[0.95rem] font-medium leading-snug">{option}</p>
            <p className="text-[0.85rem] italic text-muted-foreground">{WEIGH_PROMPT}</p>
            {notes[option] ? (
              <p className="mt-1 whitespace-pre-wrap border-l-2 border-muted pl-3 text-[0.95rem]">
                {notes[option]}
              </p>
            ) : (
              <WritingLines count={roomy ? 4 : 2} />
            )}
          </div>
        ))}
        {leftover.length > 0 && (
          <p className="whitespace-pre-wrap border-l-2 border-muted pl-3 text-[0.95rem]">
            {leftover.join(" · ")}
          </p>
        )}
        <PrintedCounter question={question} showAnswers={showAnswers} roomy={roomy} />
      </div>
    );
  }

  const multi = question.answerType === MULTI;
  const unlisted = picks.filter((pick) => !question.options.includes(pick));

  return (
    <div className="mt-2 space-y-1.5">
      {multi && (
        <p className="text-[0.85rem] italic text-muted-foreground">
          Pažymėk visus, kurie tinka.
        </p>
      )}
      {question.options.map((option) => (
        <div key={option} className="flex items-start gap-2">
          <span
            className={`mt-0.5 h-4 w-4 shrink-0 border border-foreground/70 ${
              multi ? "rounded-[0.15rem]" : "rounded-full"
            } ${picks.includes(option) ? "bg-foreground/70" : ""}`}
          />
          <span className="text-[0.95rem] leading-snug">{option}</span>
        </div>
      ))}

      {/* „Kita" prints as a box with a ruled line, so the sheet is answerable
          away from the screen — which is the whole point of printing it. */}
      {question.allowsOther && (
        <div className="flex items-baseline gap-2">
          <span
            className={`mt-0.5 h-4 w-4 shrink-0 self-start border border-foreground/70 ${
              multi ? "rounded-[0.15rem]" : "rounded-full"
            } ${unlisted.length > 0 ? "bg-foreground/70" : ""}`}
          />
          <span className="text-[0.95rem]">{OTHER_LABEL}:</span>
          <span className="flex-1 border-b border-dotted border-muted-foreground/50 text-[0.95rem]">
            {unlisted.join(" ")}
          </span>
        </div>
      )}

      {/* What they wrote when this question was still an open one. „Show what was
          already written" has to show all of it, and a pick matching no option
          would otherwise vanish off the sheet. Where „Kita" is offered the same
          text is that box's own content, printed above. */}
      {!question.allowsOther && unlisted.length > 0 && (
        <p className="whitespace-pre-wrap border-l-2 border-muted pl-3 text-[0.95rem]">
          {unlisted.join(" · ")}
        </p>
      )}

      {question.asksWhy && (
        <div className="pt-1">
          <p className="text-[0.9rem] font-medium">{WHY_LABEL}</p>
          {showAnswers && question.reasonAnswer ? (
            <p className="mt-1 whitespace-pre-wrap border-l-2 border-muted pl-3 text-[0.95rem]">
              {question.reasonAnswer}
            </p>
          ) : (
            <WritingLines count={roomy ? 5 : 2} />
          )}
        </div>
      )}

      <PrintedCounter question={question} showAnswers={showAnswers} roomy={roomy} />
    </div>
  );
}

/**
 * The peer's argument on paper. Kept inside a bordered block for the reason it has
 * one on screen: it is a claim somebody made, and printed as plain text under the
 * question it reads as part of the question — which turns "answer this" into
 * "here is the answer".
 */
function PrintedCounter({
  question,
  showAnswers,
  roomy = false,
}: {
  question: PrintedQuestion;
  showAnswers: boolean;
  roomy?: boolean;
}) {
  const counter = question.counter?.trim();
  if (!counter) return null;

  return (
    <div className="mt-3 border-l-2 border-foreground/30 pl-3">
      <p className="text-[0.85rem] italic text-muted-foreground">{COUNTER_LEAD}</p>
      <p className="text-[0.95rem] italic">{counter}</p>
      <p className="mt-1 text-[0.9rem] font-medium">{COUNTER_PROMPT}</p>
      {showAnswers && question.counterAnswer ? (
        <p className="mt-1 whitespace-pre-wrap text-[0.95rem]">{question.counterAnswer}</p>
      ) : (
        <WritingLines count={roomy ? 8 : 3} />
      )}
    </div>
  );
}

/**
 * Ruled lines rather than an empty gap. A blank space says "a bit"; lines say how
 * much room there is, and a child writing by hand needs the guide.
 */
function WritingLines({ count }: { count: number }) {
  return (
    <div className="mt-2 space-y-5">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="border-b border-dotted border-muted-foreground/50" />
      ))}
    </div>
  );
}
